import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Close as CloseIcon, LocalOffer as OfferIcon } from '@mui/icons-material';
import { Alert, Box, Button, IconButton, Typography } from '@mui/material';

import { useSubscription } from '@hooks';

/**
 * Banner component that displays grace period countdown and discount information.
 * - Shows blue info style for first 3/4 of grace period (45+ days)
 * - Shows orange warning style for last 1/4 of grace period (< 15 days)
 * - Can be dismissed but reappears on page reload
 */
export function GracePeriodBanner() {
  const [isDismissed, setIsDismissed] = useState(false);
  const navigate = useNavigate();
  const { isInGracePeriod, gracePeriodDaysRemaining, gracePeriod, isPro, isExempt, isAdmin } = useSubscription();

  // Don't show banner if:
  // - Not in grace period
  // - User dismissed it
  // - User has PRO subscription (any tier)
  // - User is EXEMPT
  // - User is ADMIN
  if (!isInGracePeriod || isDismissed || isPro || isExempt || isAdmin) {
    return null;
  }

  // Determine severity based on days remaining
  // Warning for last 1/4 of period (< 15 days out of 60)
  const severity = gracePeriodDaysRemaining < 15 ? 'warning' : 'info';
  const isUrgent = severity === 'warning';

  const handleUpgradeClick = () => {
    navigate('/pricing');
  };

  const handleDismiss = () => {
    setIsDismissed(true);
  };

  return (
    <Alert
      severity={severity}
      icon={<OfferIcon />}
      sx={{
        borderRadius: 0,
        alignItems: 'center',
        position: 'sticky',
        top: 0,
        zIndex: 1100,
        boxShadow: isUrgent ? 2 : 1
      }}
      action={
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Button color={isUrgent ? 'warning' : 'info'} variant='contained' size='small' onClick={handleUpgradeClick} sx={{ whiteSpace: 'nowrap' }}>
            Upgrade with {gracePeriod.discountPercentage}% Off
          </Button>
          <IconButton size='small' onClick={handleDismiss} sx={{ ml: 1 }}>
            <CloseIcon fontSize='small' />
          </IconButton>
        </Box>
      }
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
        <Typography variant='body2' fontWeight={600}>
          Thank you for being an early supporter!
        </Typography>
        <Typography variant='body2'>
          You have{' '}
          <strong>
            {gracePeriodDaysRemaining} {gracePeriodDaysRemaining === 1 ? 'day' : 'days'}
          </strong>{' '}
          remaining of full PRO access. Upgrade now with <strong>{gracePeriod.discountPercentage}% off</strong> to keep all features!
        </Typography>
      </Box>
    </Alert>
  );
}
